import * as React from 'react';
import './ReportDetailDep.scss';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import { SearchInput, Select, CustomPagination } from '@iba-team/portal-react-components/dist';
import { ReactComponent as SearchIcon } from '@iba-team/portal-react-components/dist/svg/outline/search.svg';
import { useEffect, useState } from 'react';
import {
  buildDownloadFile,
  onTableRowChange
} from '../../utils/functions';
import { observer } from 'mobx-react';
import { ReportGeneralDepStore } from '../../store/ReportDetailDep';
import { DataGridListItem, SelectableDivisionsListItem } from '../../types';
import { COLUMNS } from './mock';
import { BackArrow } from './BackArrow';
import { useEffectOnce, useLocalStorage, useSetState } from 'react-use';
import { Spinner, Layout } from '@iba-team/portal-react-components/dist';
import { useAnalytics, useUsageStatistics } from '../../utils/hooks';
import apiConfig from '../../api/routes';
import ExportButton from '../../utils_components/ExportButton';
import { format } from 'date-fns';
import InvalidReportAlert from '../banner/InvalidReportAlert';

export interface SearchParamsT {
  division: SelectableDivisionsListItem,
  page: number,
  perPage: number,
  query: string,
  sorting: [
    {
      name: '',
      order: ''
    }
  ] | [
    {
      name: 'employee',
      order: 'ASC'
    }
  ] | [
    {
      name: 'employee',
      order: 'DESC'
    }
  ] | [
    {
      name: 'available',
      order: 'ASC',
    }
  ] | [
    {
      name: 'available',
      order: 'DESC'
    }
  ]
}

const SHOULD_OPEN_LINK_IN_NEWTAB = true;

const ReportDetailDep = observer(() => {
  const { t } = useTranslation();
  const {
    generalDivision: { dataGrid, header, fetched },
    selectableDivisions,
    getGeneralDivision,
    getSelectableDivisions,
    downloadReport,
    isReportDownloading,
    isSavedStateFetched,
    isSelectableDivisionsFetched
  } = ReportGeneralDepStore;

  const [pageLocalStorage, setPageLocalStorage] = useLocalStorage('page_Dep', 1);

  const [searchParams, setSearchParams] = useSetState<SearchParamsT>({
    division: { hierarchyId: 0, hierarchyName: '', hierarchyPath: '' },
    page: 1,
    perPage: 10,
    query: '',
    sorting: [{ name: '', order: '' }]
  });

  const [list,setList] = useState<DataGridListItem[]>([]);

  useUsageStatistics(apiConfig.statisticsUsageService.departmentReportPage);

  useEffect(()=>{
    setList(dataGrid.list);
  },[dataGrid]);

  useEffectOnce(()=> {
    getSelectableDivisions();
  });

  useEffect(()=> {
    if(searchParams.division.hierarchyId !== 0){
      getGeneralDivision(searchParams);
    }
  },[searchParams]);

  useEffect(()=> {
    if (isSelectableDivisionsFetched) {
      if (pageLocalStorage) {
        setSearchParams({page:pageLocalStorage});
      }
      setSearchParams({perPage: selectableDivisions.perPage, division: selectableDivisions.selectedItem});
    }
  },[isSavedStateFetched, isSelectableDivisionsFetched]);

  if (!header.departmentPercentages) {
    return null;
  }

  const buildFile = () => {
    const dateForFileName = format(new Date(), 'yyyy-MM-dd');
    downloadReport(searchParams.division).then((response) => {
      return response?.blob();
    }).then((blob) => buildDownloadFile(blob, `Divisions_report_${dateForFileName}.zip`));
  };

  const renderHeader = () => {
    return {
      layoutName:  <BackArrow text={t('sectorReportsBackText')} reportType={'Dep'}/>,
      right: <div className={'table-filters'}>
        <div className={'table-filters__inner'}>
          <SearchInput debounce={1000} allowClear className={'table-filters-input'}
            onChange={event => setSearchParams({ query: event, page: 1 })}
            placeholder={t('typeToSearch')} prefix={<SearchIcon />}/>
          <Select
            uniqueElement={el => el.hierarchyId === searchParams.division.hierarchyId }
            value={searchParams?.division?.hierarchyName?.length > 0 ? searchParams.division.hierarchyName : selectableDivisions.selectedItem?.hierarchyName}
            className={'table-filters-select'}
            list={selectableDivisions.list}
            render={(item) => <div>{item.hierarchyName}</div>}
            onChangeValue={(item) => {
              setPageLocalStorage(1);
              setSearchParams({division: item});
            }}
          />
        </div>
        <ExportButton isDownloading={isReportDownloading} onClick={buildFile} />
      </div>
    };
  };

  return (
    <Layout header={renderHeader()} className={'table'}>
      <InvalidReportAlert users={dataGrid.firstNameAndLastNameOfInvalidUsers} />
      { <Table className={'antd_table'}
        onChange={(pagination, filters, sorter) => {
          const sort = onTableRowChange(pagination, filters, sorter);
          setSearchParams({ sorting: [{ name: sort.columnName, order: sort.order as unknown as '' }] });
        }}
        dataSource={list}
        loading={{
          spinning: !fetched,
          indicator: <Spinner/>,
        }}
        columns={COLUMNS(() => {
          useAnalytics('Reports', 'from_reports_to_profile', 'dep_report');
        }, SHOULD_OPEN_LINK_IN_NEWTAB)}
        pagination={false}
        scroll={{ x: 992 }}
      />}
      <CustomPagination {...{
        total: dataGrid.total,
        current: searchParams.page,
        defaultCurrent: searchParams.page,
        position: ['bottomLeft'],
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        onShowSizeChange: (page, size) => {
          setSearchParams({ page: page, perPage: size });
          setPageLocalStorage(page);
        },
        pageSize: searchParams.perPage,
        onChange: (e) => {
          setSearchParams({ page: e });
          setPageLocalStorage(e);
        },
        hideOnSinglePage: (dataGrid.total <= 10)
      }}/>
    </Layout>
  );
});

export default ReportDetailDep;
